import React from 'react';

import { debounce } from 'lodash';

import { Polling } from '_core/polling';
import * as convert from '_core/utils/convert';
import * as storage from '_core/utils/storage';
import * as cache from '_core/utils/cache';
import { useMoneyCurrencyList } from '_core/utils/api';
import { useVisitorInfo } from '_core/utils/visitorInfo';
import AddressInput from '_core/components/AddressInputNew';

import { CURRENCY_FORMAT_CHAR } from 'basic/config';


const GOODS_REST_TIMEOUT = 5 * 60000;

const WAREHOUSE_STORAGE_KEY = 'shop-warehouse';

const WAREHOUSE_CACHE = {};

const DEFAULT_CURRENCY = {
    code: '',
    title: '',
    scale: 0,
    format: CURRENCY_FORMAT_CHAR,
  };


export const CartContext = React.createContext();

export const CartProvider = ({ children }) => {
  const visitorInfo = useVisitorInfo();
  
  const [goodsState, setGoodsState] = React.useState(null);
  const [goodsRest, setGoodsRest] = React.useState({});
  const [currency, setCurrency] = React.useState(DEFAULT_CURRENCY);
  
  const currencyList = useMoneyCurrencyList((result) => result.data.list?.length && setCurrency((currency) => currency || result.data.list.find((item) => item.id === result.data.current.id) || result.data.list[0]));
  
  
  const setGoodsRestMap = (map) => map && setGoodsRest((rest) => {
      let modified = null;
      
      Object.entries(map).forEach(([key, val]) => {
        val = convert.toInt(val);
        
        if (rest[key] === val)
          return;
        
        if (!modified)
          modified = { ...rest };
        
        modified[key] = val;
      });
      
      return modified || rest;
    });
  
  
  const cart = {
    ...goodsState,
    
    // currency
    
    currency,
    
    currencyList: currencyList.data?.list || [DEFAULT_CURRENCY],
    
    findCurrency: (id) => currencyList.data?.list?.find((item) => item.id == id)
      || currency
      || currencyList.data?.list?.length && currencyList.data.list[0]
      || DEFAULT_CURRENCY,
    
    setCurrency: (id) => {
      const newCurrency = currencyList.data?.list?.find((item) => item.id == id);
      
      if (!newCurrency || newCurrency.id === currency.id)
        return;
      
      setCurrency(newCurrency);
    },
    
    // rest
    
    rest: (id) => {
      if (!id || !cart.warehouse)
        return 0;
      
      if (cart.cityData && !cart.cityRequest && !cart.cityData.isConfirmed) {
        cart.cityRequest = true;
        
        setGoodsState((state) => ({ ...state, cityRequest: true }));
      }
      
      if (cart.warehouse.is_infinity_goods)
        return 999;
      
      cache.get(
          null,
          ['shop-goods-rest', cart.warehouse.id],
          (context, reduce) => Polling.queue('delivery/warehouse/rest', { data: { [cart.warehouse.id]: reduce } }).then((data) => data[cart.warehouse.id]),
          GOODS_REST_TIMEOUT, // clearMillis
          id, // reduceKey
          100 // reduceMillis
        )
        .then(setGoodsRestMap)
        .catch((error) => {
          console.error(error);
          
          setGoodsRestMap(error.result);
        });
      
      return goodsRest[id] || 0;
    },
    
    // city
    
    get citySimple() {
      return AddressInput.buildSimple('delivery', 'city', cart.cityData);
    },
    
    cityValue: (zip = false, reverse = false) => AddressInput.buildValue('delivery', 'city', cart.cityData, zip, reverse),
    
    setCityData: debounce((cityData) => {
      const cityChanged = AddressInput.buildValue('delivery', 'city', cityData) !== AddressInput.buildValue('delivery', 'city', cart.cityData);
      
      if (!cityChanged && cityData.isConfirmed === cart.cityData.isConfirmed)
        return;
      
      if (cityChanged)
        cart.setWarehouse(null);
      
      setGoodsState((state) => ({ ...state, cityData, cityRequest: cityData?.isConfirmed ? false : state.cityRequest }));
      
      if (!cityData)
        return;
      
      if (cityData.isConfirmed)
        AddressInput.store(cityData);
    }, 10),
    
    cityConfirm: () => cart.setCityData(cart.cityData ? { ...cart.cityData, isConfirmed: true } : null),
    
    // warehouse
    
    setWarehouse: debounce((warehouse) => {
      if (warehouse?.id === cart.warehouse?.id)
        return;
      
      setGoodsState((state) => ({ ...state, warehouse }));
      
      if (warehouse)
        storage.set(WAREHOUSE_STORAGE_KEY, warehouse.id);
    }, 10)
    
    //
  };
  
  const city = cart.cityValue();
  
  cart.warehouseList = cache.use(
      WAREHOUSE_CACHE,
      city,
      (context) => Polling.queue('delivery/warehouse/list', { city }).then((data) => data.list || []),
      (result) => {
        if (result.error || !result.data.length || cart.warehouse)
          return;
        
        const warehouseId = storage.get(WAREHOUSE_STORAGE_KEY);
        
        const warehouse = result.data.find((item) => item.id == warehouseId);
        
        cart.setWarehouse(warehouse || result.data[0]);
      }
    ) || null;
  
  React.useEffect(() => {
    if (!visitorInfo.info?.id)
      return;
    
    const startState = { update: 0 };
    
    setGoodsState(startState);
    
    AddressInput.locate([visitorInfo.info.country.title, visitorInfo.info.region.title, visitorInfo.info.city.title].join(', '))
      .then((data) => cart.setCityData(data))
      .catch((error) => cart.setCityData(null));
  }, [visitorInfo.info?.id]);
  
  return (
    <CartContext.Provider value={cart}>
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => {
  const context = React.useContext(CartContext);
  
  if (!context)
    throw new Error('useCartContext should be used inside the CartProvider');
  
  return context;
};
